import { useTranslation } from "../../hooks/useTranslation";
import type { Locale } from "../../i18n";

const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Español",
  hi: "हिन्दी",
  zh: "中文",
};

interface Props {
  className?: string;
}

/**
 * Accessible language picker. Renders a labelled native <select> so it works
 * with keyboards, screen readers, and right-click translate without extra
 * dependencies.
 */
export function LanguageSwitcher({ className = "" }: Props) {
  const { locale, setLocale, supportedLocales } = useTranslation();

  return (
    <label
      className={`relative inline-flex items-center ${className}`}
      aria-label="Language"
    >
      <span className="sr-only">Language</span>
      <select
        value={locale}
        onChange={(e) => setLocale(e.target.value as Locale)}
        className="h-8 rounded-lg bg-bg-surface text-xs text-text-secondary border border-border px-2 pr-6 cursor-pointer hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary appearance-none"
      >
        {supportedLocales.map((l) => (
          <option key={l} value={l}>
            {LOCALE_LABELS[l]}
          </option>
        ))}
      </select>
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute right-1.5 w-3 h-3 text-text-muted"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M3 4.5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </label>
  );
}
